// Which of a tenant's bookings is their LIVE one? Single source of truth for
// every tenant-facing surface — do not reimplement this choice elsewhere.
//
// Why this file exists
// -------------------
// A tenant can hold several `bookings` rows: they applied for two or three
// rooms, signed for one, and an admin archived the rest. Four tenant surfaces
// each answered "which one is theirs" their own way — three different sort keys
// and four different status sets — and the move-in protocol page's copy
// (`useActiveBooking`) forgot to exclude archived rows.
//
// That mattered because the two archiving paths disagree. The archive-on-sign
// trigger (20260719100000) sets `status='archived'` AND `admin_archived_at`, so
// a status filter alone happens to drop those rows. The admin console's manual
// Archive toggle stamps ONLY `admin_archived_at` and leaves `status` as
// `pending`/`confirmed`. So hand-archived leftovers sailed through a
// status-only filter, and because the protocol page took whichever row had the
// latest `start_date`, a dead application — one carrying a placeholder start
// date years in the future — outranked the real tenancy and became the booking
// the tenant's protocol card described. Admins reopening a protocol wrote the
// reopen stamp to the real booking; the tenant's page was reading the archived
// one, so reopening looked like it silently did nothing.
//
// Two rules, so that class of bug cannot come back:
//   1. `admin_archived_at` is THE archive flag. Never infer "archived" from
//      `status` alone.
//   2. Rank by where the tenant actually is in the tenancy, not by date. A
//      confirmed tenancy beats a pending application even when the application
//      carries the later date — dates are tenant-supplied and drift; the
//      lifecycle stage is what staff act on.

/**
 * Statuses worth querying for "the tenant's live booking": an ongoing tenancy,
 * or an application that could still become one. Closed and archived statuses
 * are deliberately absent — see `CLOSED_STATUSES`.
 *
 * Pass this to `.in("status", …)` so the row set the ranking sees is already
 * the right shape.
 */
export const TENANT_LIVE_BOOKING_STATUSES = [
  "pending",
  "approved",
  "confirmed",
  "active",
  "notice_given",
  "move_out_pending",
] as const;

/** This booking is over, or was never a tenancy. Never the live one. */
const CLOSED_STATUSES: ReadonlySet<string> = new Set([
  "moved_out",
  "terminated",
  "cancelled",
  "archived",
]);

/**
 * How far along the tenancy each status sits. Higher wins.
 *
 * Deliberately coarse: `active`/`notice_given`/`move_out_pending` are all "they
 * live there", and the tie between them is settled by the later keys rather
 * than by inventing an ordering between a notice and a pending move-out.
 *
 * `signature_pending`/`draft` are real `bookings.status` values (see the
 * `bookings_status_check` constraint) that no current caller's `.in(...)`
 * filter actually passes through — `TENANT_LIVE_BOOKING_STATUSES` starts at
 * `pending`. Ranked here anyway so a caller that widens its filter to include
 * these earlier stages gets a sane answer instead of falling through to 0.
 */
const STAGE_RANK: Readonly<Record<string, number>> = {
  active: 3,
  notice_given: 3,
  move_out_pending: 3,
  confirmed: 2,
  approved: 2,
  signature_pending: 1,
  pending: 1,
  draft: 1,
};

/** The columns the ranking reads. Every caller must select all of them. */
export type LiveBookingCandidate = {
  id: number;
  status?: string | null;
  start_date?: string | null;
  admin_archived_at?: string | null;
  contract_signed_at?: string | null;
};

/** Could this row be the tenant's live booking at all? */
export function isLiveTenantBooking(
  booking: LiveBookingCandidate | null | undefined
): boolean {
  if (!booking) return false;
  // Rule 1: the archive stamp is the archive flag, whatever `status` says.
  if (booking.admin_archived_at) return false;
  return !CLOSED_STATUSES.has((booking.status ?? "").toLowerCase());
}

function stageRank(booking: LiveBookingCandidate): number {
  return STAGE_RANK[(booking.status ?? "").toLowerCase()] ?? 0;
}

/**
 * Pick the tenant's live booking from every row they hold.
 *
 * Order of preference:
 *   1. Furthest along the tenancy (`STAGE_RANK`).
 *   2. Has a signed contract.
 *   3. Latest `start_date` (rows with no start date rank last).
 *   4. Highest `id` — a deterministic tiebreak, so the same tenant never sees
 *      a different answer between two renders or two devices.
 *
 * Returns null when the tenant holds nothing live.
 */
export function pickLiveTenantBooking<T extends LiveBookingCandidate>(
  rows: readonly T[] | null | undefined
): T | null {
  const live = (rows ?? []).filter(isLiveTenantBooking);
  if (!live.length) return null;
  return [...live].sort(byLiveness)[0] ?? null;
}

/** Sort comparator: the live booking sorts first. */
function byLiveness(a: LiveBookingCandidate, b: LiveBookingCandidate): number {
  const byStage = stageRank(b) - stageRank(a);
  if (byStage !== 0) return byStage;

  const signed = (r: LiveBookingCandidate) => (r.contract_signed_at ? 1 : 0);
  const bySigned = signed(b) - signed(a);
  if (bySigned !== 0) return bySigned;

  // ISO dates compare correctly as strings; "" sorts last, which is what we
  // want for a row with no start date at all.
  const start = (r: LiveBookingCandidate) => r.start_date ?? "";
  if (start(a) !== start(b)) return start(a) < start(b) ? 1 : -1;

  return b.id - a.id;
}
